import Clarity from '@microsoft/clarity';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ConfirmDialog from 'common/ComfirmationDialog';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from 'react-redux';
import { setAppError } from 'store/actions/appstate';
import { AppConfig } from './AppConfig';
import './assets/css/custom.css';
import AppRoute from './routes/AppRoute';
import usePermissionsRefreshTimer from 'common/hooks/usePermissionsRefreshTimer'
import * as UserTypes from 'features/user/userTypes';
import LocalStore from 'utils/LocalStore';
import routePaths from 'routes/routePaths';

const TOKEN_EXPIRED_LOGOUT_FLAG = 'bi_token_expired_logout_in_progress';

ReactGA.initialize(AppConfig.GOOGLE_ANALYTICS_ID, {
  debug: true
});
ReactGA.send('pageview');

const clarityTrackingId = AppConfig.CLARITY_TRACKING_ID;
Clarity.init(clarityTrackingId);

const App = () => {

  // During an authenticated app/tenant switch the tenant is momentarily empty and the
  // app-shell loading skeleton takes over the screen. Suppress the global network spinner
  // in that window so the skeleton (not the spinner) is what the user sees. On unauthenticated
  // routes (login/SSO) there is no token, so the spinner still shows as before.

  const messageSeverity = useSelector(
    (state) => state.appState.messageSeverity
  );
  const appError = useSelector((state) => state.appState.appError);

  const dispatch = useDispatch();

  const onCloseError = () => {
    dispatch(setAppError(null));
  };

  const isNonEmptyMessage = (obj) => {
    if (typeof obj === 'string' && obj.trim() !== '') {
      return true;
    }
    return obj && obj.message && obj.message.trim() !== '';
  };

  useEffect(() => {
    moment.tz.setDefault('utc');
  }, []);

  useEffect(() => {
    const isOnLogoutRoute = window.location.pathname.startsWith(routePaths.logout);
    const isTokenExpiryLogoutInProgress =
      sessionStorage.getItem(TOKEN_EXPIRED_LOGOUT_FLAG) === '1';

    if (isOnLogoutRoute || isTokenExpiryLogoutInProgress) {
      return;
    }

    const getTokenExpirationMs = (token) => {
      if (!token) return null;
      try {
        const decoded = jwtDecode(token);
        return decoded?.exp ? decoded.exp * 1000 : null;
      } catch (error) {
        return null;
      }
    };

    let isExpiryLogoutTriggered = false;

    const logoutIfTokenExpired = () => {
      if (isExpiryLogoutTriggered) {
        return;
      }

      const token = LocalStore.getAccessToken();
      const expirationMs = getTokenExpirationMs(token);

      if (expirationMs && expirationMs <= Date.now()) {
        isExpiryLogoutTriggered = true;
        sessionStorage.setItem(TOKEN_EXPIRED_LOGOUT_FLAG, '1');
        LocalStore.clear();
        dispatch({ type: UserTypes.LOGOUT });
        window.location.href = `${routePaths.logout}?reason=tokenExpired`;
      }
    };

    // Check immediately, then keep a lightweight periodic check for idle tabs.
    logoutIfTokenExpired();
    const intervalId = setInterval(logoutIfTokenExpired, 30 * 1000);

    return () => clearInterval(intervalId);
  }, [dispatch]);

  usePermissionsRefreshTimer();

  //use Clarity
  useEffect(() => {
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r);
        t.async = 1;
        t.src = `https://www.clarity.ms/tag/${i}`;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      }(window, document, 'clarity', 'script', clarityTrackingId));
  }, []);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <>
          {isNonEmptyMessage(appError) && (
            <Snackbar
              open={true}
              onClose={onCloseError}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={15000}>
              <Alert severity={messageSeverity} onClose={onCloseError}>
                {appError}
              </Alert>
            </Snackbar>
          )}
          {<AppRoute />}
        </>
      </LocalizationProvider>
      <ConfirmDialog />
    </>
  );
};

export default App;
